import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UiIcon from '../../components/common/UiIcon';
import CheckoutComplianceNotice from '../../components/common/CheckoutComplianceNotice';
import { useReadingFlowBackGuard } from '../../lib/navigationGuard';
import { trackEvent } from '../../lib/analytics';
import {
  getWalletSummary,
  spendWalletForReading,
  startKakaoPayReady,
} from '../../lib/api';
import type { SpreadType, Topic, WalletPackage, WalletSummaryResponse } from '../../lib/types';

const PENDING_READING_KEY = 'ny_pending_payment_reading';
const KAKAOPAY_PENDING_KEY = 'ny_kakaopay_pending_checkout';

type PaymentMode = 'disabled' | 'mock' | 'live';
type PaymentProvider = 'kakaopay';

interface PaymentConfigResponse {
  paymentMode: PaymentMode;
  provider: PaymentProvider;
  providerStatus: 'pending' | 'ready';
  isConfigured: boolean;
}

interface PendingReadingState {
  readingId: string;
  replayToken: string;
  spread?: SpreadType;
}

interface PendingKakaoPayState {
  orderType: 'wallet-recharge';
  packageId: string;
  readingId?: string;
  orderId: string;
  tid: string;
  amount: number;
  spread?: SpreadType;
}

function readPendingState(readingId: string): PendingReadingState | null {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(PENDING_READING_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingReadingState;
    if (parsed.readingId === readingId && parsed.replayToken) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
}

function clearPendingState(readingId: string) {
  if (typeof window === 'undefined') return;

  const raw = window.sessionStorage.getItem(PENDING_READING_KEY);
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as PendingReadingState;
    if (parsed.readingId === readingId) {
      window.sessionStorage.removeItem(PENDING_READING_KEY);
    }
  } catch {
    window.sessionStorage.removeItem(PENDING_READING_KEY);
  }
}

function pickRechargePackage(packages: WalletPackage[], shortagePoints: number): WalletPackage | null {
  if (!packages.length) return null;

  const sorted = [...packages].sort((a, b) => a.points - b.points);
  const fitted = sorted.find((entry) => entry.points >= shortagePoints);
  return fitted ?? sorted[sorted.length - 1] ?? null;
}

export const PaymentCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfigResponse | null>(null);
  const [wallet, setWallet] = useState<WalletSummaryResponse | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningReading, setIsOpeningReading] = useState(false);
  const [isRecharging, setIsRecharging] = useState(false);

  const readingId = searchParams.get('readingId') || '';
  const spread = (searchParams.get('spread') as SpreadType) || 'three-card';
  const recharged = searchParams.get('recharged') === '1';
  const preselectedPackageId = searchParams.get('packageId') || '';

  const pendingState = readingId ? readPendingState(readingId) : null;
  const replayToken = pendingState?.replayToken || '';

  const selectedQuestion = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem('selected_question') || '';
  }, []);

  const selectedTopic = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem('selected_theme') as Topic | null;
  }, []);

  useReadingFlowBackGuard(Boolean(readingId));

  useEffect(() => {
    trackEvent('payment_checkout_opened', {
      spread,
      reading_id_prefix: readingId.slice(0, 8),
      recharged,
    });
  }, [readingId, recharged, spread]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const [configResponse, walletResponse] = await Promise.all([
          fetch('/api/payment/config', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          }),
          getWalletSummary(),
        ]);

        if (!configResponse.ok) {
          throw new Error(`payment config ${configResponse.status}`);
        }

        const config = await configResponse.json() as PaymentConfigResponse;
        if (cancelled) return;

        setPaymentConfig(config);
        setWallet(walletResponse);
      } catch (error) {
        console.error('Failed to load checkout data:', error);
        if (!cancelled) {
          setErrorMessage('결제 준비 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  const spreadCost = wallet?.spreadCosts?.[spread] ?? 0;
  const balancePoints = wallet?.balancePoints ?? 0;
  const freeReadings = wallet?.freeReadings ?? { 'one-card': 0, 'three-card': 0, 'celtic-cross': 0 };
  const freeReadingCount = freeReadings[spread] ?? 0;
  const hasFreeReading = freeReadingCount > 0;
  const shortagePoints = Math.max(0, spreadCost - balancePoints);
  const hasEnoughPoints = spreadCost > 0 && balancePoints >= spreadCost;
  const hasEnoughAccess = hasEnoughPoints || hasFreeReading;
  const paymentMode = paymentConfig?.paymentMode ?? 'disabled';
  const provider = paymentConfig?.provider ?? 'kakaopay';
  const providerReady = paymentConfig?.providerStatus === 'ready';
  const isMockKakaoFlow = paymentMode === 'mock' && provider === 'kakaopay';
  const canStartKakaoRecharge =
    provider === 'kakaopay' && paymentMode !== 'disabled' && (isMockKakaoFlow || providerReady);
  const selectedPackage = wallet?.packages.find((entry) => entry.id === selectedPackageId) ?? null;
  const spreadLabel =
    spread === 'one-card'
      ? '원카드 리딩'
      : spread === 'three-card'
        ? '쓰리카드 리딩'
        : '켈틱 크로스 리딩';
  const sessionSummaryLines =
    spread === 'one-card'
      ? [
          '지금 가장 먼저 봐야 할 답을 한 장으로 짚습니다.',
          '100원 = 1츄르 기준으로 10츄르를 사용합니다.',
        ]
      : spread === 'three-card'
        ? [
            '시작점과 현재의 핵심, 다음 방향을 세 장으로 따라갑니다.',
            '100원 = 1츄르 기준으로 15츄르를 사용합니다.',
          ]
        : [
            '상황 구조와 바깥 변수, 마지막 결말까지 깊게 펼쳐봅니다.',
            '100원 = 1츄르 기준으로 20츄르를 사용합니다.',
          ];
  const pointCostLabel = `${spreadCost}츄르`;

  useEffect(() => {
    if (!wallet?.packages?.length) return;

    if (
      preselectedPackageId
      && wallet.packages.some((entry) => entry.id === preselectedPackageId)
      && preselectedPackageId !== selectedPackageId
    ) {
      setSelectedPackageId(preselectedPackageId);
      return;
    }

    if (selectedPackageId && wallet.packages.some((entry) => entry.id === selectedPackageId)) {
      return;
    }

    const nextPackage = pickRechargePackage(wallet.packages, Math.max(shortagePoints, 1));
    if (nextPackage) {
      setSelectedPackageId(nextPackage.id);
    }
  }, [preselectedPackageId, selectedPackageId, shortagePoints, wallet?.packages]);

  useEffect(() => {
    if (!recharged) return;

    if (hasEnoughAccess) {
      setNotice('츄르 충전이 끝났습니다. 아래 버튼으로 바로 이어가면 됩니다.');
      return;
    }

    setNotice('충전 결과를 불러왔습니다. 패키지와 보유량을 한 번만 더 확인해 주세요.');
  }, [hasEnoughAccess, recharged]);

  const openReading = async () => {
    if (!readingId || isOpeningReading) return null;

    if (!replayToken) {
      setErrorMessage('리딩 세션 정보가 만료되었습니다. 질문을 다시 골라 주세요.');
      return null;
    }

    setIsOpeningReading(true);
    setErrorMessage('');

    try {
      const result = await spendWalletForReading(readingId, spread);

      window.sessionStorage.setItem(`reading_${readingId}`, replayToken);
      clearPendingState(readingId);
      window.sessionStorage.setItem('selected_spread', spread);
      navigate(`/spread-layout/${readingId}`);

      return result;
    } catch (error) {
      const apiError = error as Error & { code?: string };

      if (apiError.code === 'INSUFFICIENT_POINTS') {
        setErrorMessage('츄르가 부족합니다. 먼저 충전한 뒤 다시 이어 주세요.');
      } else if (apiError.code === 'ALREADY_PROCESSING') {
        window.sessionStorage.setItem(`reading_${readingId}`, replayToken);
        navigate(`/spread-layout/${readingId}`);
      } else {
        setErrorMessage(apiError.message || '리딩을 여는 중 문제가 발생했습니다.');
      }
      return null;
    } finally {
      setIsOpeningReading(false);
    }
  };

  const handleLiveRecharge = async () => {
    if (!selectedPackage || !readingId || isRecharging) return;

    trackEvent('payment_started', {
      source: 'payment_checkout',
      package_id: selectedPackage.id,
      amount_krw: selectedPackage.amountKrw,
      points: selectedPackage.points,
      spread,
    });

    setIsRecharging(true);
    setErrorMessage('');

    try {
      if (isMockKakaoFlow) {
        const mockParams = new URLSearchParams({
          provider: 'kakaopay-demo',
          mode: 'wallet-recharge',
          packageId: selectedPackage.id,
          amount: String(selectedPackage.amountKrw),
          points: `${selectedPackage.points}츄르`,
          label: selectedPackage.label,
          readingId,
          spread,
          next: `/payment/checkout?readingId=${encodeURIComponent(readingId)}&spread=${encodeURIComponent(spread)}&recharged=1&packageId=${encodeURIComponent(selectedPackage.id)}`,
        });

        navigate(`/payment/mock?${mockParams.toString()}`);
        return;
      }

      const ready = await startKakaoPayReady({
        orderType: 'wallet-recharge',
        packageId: selectedPackage.id,
        readingId,
        spread,
      });

      const pendingCheckoutState: PendingKakaoPayState = {
        orderType: 'wallet-recharge',
        packageId: selectedPackage.id,
        readingId,
        orderId: ready.orderId,
        tid: ready.tid,
        amount: selectedPackage.amountKrw,
        spread,
      };

      window.sessionStorage.setItem(KAKAOPAY_PENDING_KEY, JSON.stringify(pendingCheckoutState));

      const isMobile =
        /Mobi|Android|iPhone|iPad/i.test(window.navigator.userAgent) || window.innerWidth < 768;
      const redirectUrl = isMobile
        ? ready.nextRedirectMobileUrl || ready.nextRedirectPcUrl || ready.nextRedirectAppUrl
        : ready.nextRedirectPcUrl || ready.nextRedirectMobileUrl || ready.nextRedirectAppUrl;

      if (!redirectUrl) {
        throw new Error('카카오페이 이동 주소를 받지 못했습니다.');
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      const apiError = error as Error;
      setErrorMessage(apiError.message || '카카오페이 충전 준비에 실패했습니다.');
      setIsRecharging(false);
    }
  };

  if (!readingId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#110d0c] px-4 text-[#f7ecde]">
        <div className="w-full max-w-md border border-[#fbbf24]/20 bg-[#1b1411]/92 p-7 text-center shadow-2xl">
          <p className="text-lg font-semibold text-[#f8ead6]">리딩 정보가 없습니다.</p>
          <p className="mt-3 text-sm leading-7 text-[#d9c0a4]/82">
            질문과 스프레드를 다시 고른 뒤 결제 화면으로 이동해 주세요.
          </p>
          <button
            type="button"
            onClick={() => navigate('/spreads')}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-[#8a6a4b] px-5 py-3 text-sm text-[#f7ecde] hover:bg-[#241914]"
          >
            <UiIcon name="arrow-left" className="h-4 w-4" />
            스프레드 다시 고르기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#110d0c] px-4 py-8 text-[#f7ecde] sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#fbbf24]/66">츄르로 여는 리딩</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-[0.02em] text-[#fbf3e8] sm:text-4xl">
              츄르를 채우고 리딩을 이어갑니다
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-[#8a6a4b]/55 bg-[#1a1310]/80 px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-[#ead7bf] hover:bg-[#271b16]"
          >
            <UiIcon name="arrow-left" className="h-4 w-4" />
            이전 단계
          </button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.96fr)_minmax(360px,1.04fr)] xl:gap-8">
          <section className="border border-[#fbbf24]/18 bg-[#211713]/92 p-5 shadow-[0_24px_70px_-30px_rgba(0,0,0,0.95)] backdrop-blur-md sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#fbbf24]/55">세션 요약</p>
                <h2 className="mt-3 text-2xl font-semibold tracking-[0.03em] text-[#fbf2e7]">
                  {spreadLabel}
                </h2>
                <div className="mt-3 space-y-1 text-sm leading-7 text-[#e7d1b7]/82">
                  {sessionSummaryLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </div>
              <span className="inline-flex items-center rounded-full border border-[#fbbf24]/30 bg-[#fbbf24]/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#f8d69a]">
                {pointCostLabel}
              </span>
            </div>

            <div className="mt-6 grid gap-4">
              <article className="border border-[#fbbf24]/14 bg-[#1b1411]/90 p-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">질문</p>
                <p className="mt-3 text-[15px] leading-7 text-[#f7ecde]">
                  {selectedQuestion || '지금 가장 먼저 봐야 할 답을 여는 리딩'}
                </p>
              </article>

              <div className="grid gap-4 sm:grid-cols-2">
                <article className="border border-[#fbbf24]/14 bg-[#1b1411]/90 p-4">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">리딩 주제</p>
                  <p className="mt-3 text-sm leading-7 text-[#f7ecde]">{selectedTopic ?? '종합'}</p>
                </article>
                <article className="border border-[#fbbf24]/14 bg-[#1b1411]/90 p-4">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">세션 번호</p>
                  <p className="mt-3 break-all text-sm leading-7 text-[#f7ecde]">#{readingId.slice(0, 8)}</p>
                </article>
              </div>

              <article className="border border-[#fbbf24]/14 bg-[#1b1411]/90 p-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">이번 세션 비용</p>
                <div className="mt-3 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-2xl font-semibold text-[#fbf2e7]">{spreadCost}츄르</p>
                    <p className="mt-1 text-sm text-[#cdb99e]/78">100원 = 1츄르 기준</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#fbbf24]/50">현재 보유</p>
                    <p className="mt-1 text-xl font-semibold text-[#fbbf24]">{balancePoints}츄르</p>
                  </div>
                </div>
              </article>

              <article className="border border-[#fbbf24]/14 bg-[#1b1411]/90 p-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/50">이번 세션을 여는 방식</p>
                <div className="mt-3 grid gap-2 text-sm text-[#f7ecde]">
                  <div className="flex items-start gap-2">
                    <UiIcon name="check" className="mt-0.5 h-4 w-4 text-[#fbbf24]/72" />
                    <span>리딩을 열 때 선택한 스프레드만큼 츄르가 차감됩니다.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <UiIcon name="check" className="mt-0.5 h-4 w-4 text-[#fbbf24]/72" />
                    <span>충전은 카카오페이로 진행하고, 결제 직후 다시 돌아옵니다.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <UiIcon name="check" className="mt-0.5 h-4 w-4 text-[#fbbf24]/72" />
                    <span>충전이 끝나면 같은 질문과 스프레드로 이어갑니다.</span>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section className="border border-[#fbbf24]/18 bg-[#f8f5f0] p-5 text-[#18110e] shadow-[0_24px_70px_-30px_rgba(0,0,0,0.95)] sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#8a6a4b]">츄르 지갑</p>
                <h2 className="mt-2 text-3xl font-semibold text-[#18110e]">지금 가진 츄르부터 확인할게요</h2>
              </div>
              <div className="rounded-2xl border border-[#eadfce] bg-white px-4 py-3 text-right shadow-sm">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#8a6a4b]">현재 잔액</p>
                <p className="mt-2 text-2xl font-semibold text-[#18110e]">{balancePoints}츄르</p>
              </div>
            </div>

            {notice && (
              <div className="mt-5 rounded-2xl border border-[#dbeed1] bg-[#f3faf0] px-4 py-3 text-sm leading-6 text-[#2a5d2c]">
                {notice}
              </div>
            )}

            {errorMessage && (
              <div className="mt-5 rounded-2xl border border-[#f4d4cf] bg-[#fff5f3] px-4 py-3 text-sm leading-6 text-[#8b3d34]">
                {errorMessage}
              </div>
            )}

            {isLoading ? (
              <div className="mt-6 rounded-3xl border border-[#eadfce] bg-white/90 px-5 py-10 text-center shadow-sm">
                <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#fbbf24]/25 border-t-[#fbbf24]" />
                <p className="mt-4 text-sm text-[#6a5448]">지갑 정보와 결제 설정을 불러오는 중입니다.</p>
              </div>
            ) : (
              <>
                <div className="mt-6 grid gap-4 lg:grid-cols-[0.98fr_1.02fr]">
                  <article className="rounded-3xl border border-[#eadfce] bg-white px-5 py-5 shadow-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a6a4b]">이번 세션에 필요한 츄르</p>
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <div>
                        <p className="text-3xl font-semibold text-[#18110e]">{spreadCost}츄르</p>
                        <p className="mt-2 text-sm text-[#6a5448]">{spreadLabel} 한 번에 필요한 양입니다.</p>
                      </div>
                      {!hasEnoughAccess && (
                        <div className="rounded-2xl border border-[#f6d8c4] bg-[#fff3ea] px-4 py-3 text-right">
                          <p className="text-[10px] uppercase tracking-[0.18em] text-[#a16830]">부족한 양</p>
                          <p className="mt-1 text-xl font-semibold text-[#bd5b15]">{shortagePoints}츄르</p>
                        </div>
                      )}
                    </div>
                  </article>

                  <article className="rounded-3xl border border-[#eadfce] bg-white px-5 py-5 shadow-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a6a4b]">추천 동선</p>
                    <div className="mt-3 space-y-2 text-sm leading-7 text-[#3f3027]">
                      {hasEnoughAccess ? (
                        <>
                          <p>지금 가진 츄르만으로 바로 열 수 있습니다.</p>
                          <p>추가 결제 없이 카드 공개와 결과 페이지로 이어집니다.</p>
                        </>
                      ) : (
                        <>
                          <p>먼저 츄르를 채운 뒤 이 화면으로 돌아옵니다.</p>
                          <p>보유량만 확인하고 바로 이어가면 됩니다.</p>
                        </>
                      )}
                    </div>
                  </article>
                </div>

                {!hasEnoughAccess && (
                  <div className="mt-6 rounded-3xl border border-[#eadfce] bg-white px-5 py-5 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a6a4b]">츄르 충전</p>
                        <h3 className="mt-2 text-xl font-semibold text-[#18110e]">부족한 만큼만 먼저 채워둘게요</h3>
                      </div>
                      <span className="rounded-full border border-[#d9cab8] px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#8a6a4b]">
                        카카오페이 충전
                      </span>
                    </div>

                    <div className="mt-5 grid gap-3">
                      {(wallet?.packages ?? []).map((entry) => {
                        const active = entry.id === selectedPackageId;
                        return (
                          <button
                            key={entry.id}
                            type="button"
                            onClick={() => setSelectedPackageId(entry.id)}
                            className={`rounded-3xl border px-5 py-4 text-left transition ${
                              active
                                ? 'border-[#18110e] bg-[#18110e] text-white shadow-lg'
                                : 'border-[#e6dacc] bg-[#fcfaf7] text-[#241811] hover:border-[#cdb79c]'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-lg font-semibold">{entry.label}</p>
                                  {entry.recommended && (
                                    <span
                                      className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${
                                        active ? 'bg-white/12 text-[#f8d69a]' : 'bg-[#f5e3c8] text-[#8a5b1f]'
                                      }`}
                                    >
                                      추천
                                    </span>
                                  )}
                                </div>
                                <p className={`mt-2 text-sm leading-6 ${active ? 'text-white/78' : 'text-[#6a5448]'}`}>
                                  {entry.description}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-semibold">{entry.amountKrw.toLocaleString()}원</p>
                                <p className={`mt-1 text-xs ${active ? 'text-white/68' : 'text-[#8a6a4b]'}`}>100원 = 1츄르</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-6 rounded-3xl border border-[#eadfce] bg-white px-5 py-5 shadow-sm">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a6a4b]">실행</p>
                  <div className="mt-4 grid gap-3">
                    {hasEnoughAccess ? (
                      <button
                        type="button"
                        onClick={() => void openReading()}
                        disabled={isOpeningReading}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#18110e] px-5 py-4 text-base font-semibold text-white transition hover:bg-[#241811] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <UiIcon name="sparkles" className="h-5 w-5" />
                        {isOpeningReading ? '세션을 여는 중...' : `${spreadCost}츄르로 세션 열기`}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleLiveRecharge()}
                        disabled={!selectedPackage || isRecharging || !canStartKakaoRecharge}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#f7de52] px-5 py-4 text-base font-semibold text-[#241811] transition hover:bg-[#efd33e] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <UiIcon name="coin" className="h-5 w-5" />
                        {isRecharging
                          ? '카카오페이로 이동하는 중...'
                          : `카카오페이로 ${selectedPackage?.label ?? '츄르'} 충전`}
                      </button>
                    )}

                    {!hasEnoughAccess && !isMockKakaoFlow && paymentMode !== 'disabled' && provider === 'kakaopay' && !providerReady && (
                      <p className="text-sm leading-6 text-[#8b3d34]">
                        카카오페이 설정이 아직 준비되지 않았습니다. 관리자 설정을 먼저 확인해 주세요.
                      </p>
                    )}

                    {paymentMode === 'disabled' && (
                      <p className="text-sm leading-6 text-[#8b3d34]">
                        현재는 결제를 받을 수 없는 설정입니다. 잠시 후 다시 시도해 주세요.
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            <CheckoutComplianceNotice className="mt-6" />
          </section>
        </div>
      </div>
    </div>
  );
};
